// ============================================================================
// ValueProps — the "why trainers switch" section. Three benefit cards that
// reveal on scroll, plus a row of animated count-up stats (reusing the locked
// <StatTile/>, whose number tween fires once in view and snaps under reduced
// motion). Mobile-first: a single column at 390, three-up from md.
// ============================================================================
import { useTranslation } from "react-i18next";
import { motion, useReducedMotion } from "framer-motion";
import type { Variants } from "framer-motion";
import { Card, StatTile } from "../../components";

const EASE = [0.22, 0.61, 0.36, 1] as const;

const container: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1 } },
};
const rise: Variants = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: EASE } },
};

function ClockIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-7 w-7" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="2" />
      <path d="M12 7.5V12l3 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function HeartIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-7 w-7" fill="none" aria-hidden>
      <path
        d="M12 20s-7-4.6-7-9.4A4.1 4.1 0 0 1 12 8a4.1 4.1 0 0 1 7 2.6C19 15.4 12 20 12 20Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
    </svg>
  );
}
function GrowthIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-7 w-7" fill="none" aria-hidden>
      <path d="M4 18 9 11l3.5 3.5L20 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M15 6h5v5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const VALUE_KEYS = [
  { key: "save", grad: "bg-grad-coral", shadow: "shadow-volt", icon: <ClockIcon /> },
  { key: "retain", grad: "bg-grad-violet", shadow: "shadow-ember", icon: <HeartIcon /> },
  { key: "grow", grad: "bg-grad-lime", shadow: "shadow-lime", icon: <GrowthIcon /> },
] as const;

export function ValueProps() {
  const { t } = useTranslation("landing");
  const reduce = useReducedMotion();

  return (
    <section id="why" className="relative scroll-mt-20 bg-paper py-16 sm:py-20 lg:py-24">
      <div className="mx-auto max-w-shell px-gutter">
        <motion.div
          initial={reduce ? false : { opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.5, ease: EASE }}
          className="mx-auto max-w-2xl text-center"
        >
          <p className="font-display text-sm font-bold uppercase tracking-kicker text-coral">
            {t("values.kicker")}
          </p>
          <h2 className="mt-3 font-display font-extrabold tracking-tightest text-body text-[clamp(1.9rem,1.2rem+3vw,3rem)]">
            {t("values.title")}
          </h2>
          <p className="mt-4 text-base leading-relaxed text-body-muted sm:text-lg">
            {t("values.subtitle")}
          </p>
        </motion.div>

        <motion.div
          variants={reduce ? undefined : container}
          initial={reduce ? undefined : "hidden"}
          whileInView={reduce ? undefined : "show"}
          viewport={{ once: true, amount: 0.2 }}
          className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-3"
        >
          {VALUE_KEYS.map(({ key, grad, shadow, icon }) => (
            <motion.div key={key} variants={reduce ? undefined : rise}>
              <Card interactive className="h-full">
                <span
                  className={
                    "mb-5 grid h-14 w-14 place-items-center rounded-md text-white " +
                    grad +
                    " " +
                    shadow
                  }
                >
                  {icon}
                </span>
                <h3 className="font-display text-xl font-bold text-body">
                  {t(`values.items.${key}.title`)}
                </h3>
                <p className="mt-2 text-body-muted">
                  {t(`values.items.${key}.body`)}
                </p>
              </Card>
            </motion.div>
          ))}
        </motion.div>

        {/* Animated count-up proof stats. Real, sensible values so the tiles
            never read as a broken "0": a 3× faster-logging multiplier, a
            sub-second (effectively instant) cross-device sync, and a 100%
            in-browser figure. Each target is > 0 so StatTile's count-up tween
            animates up to a meaningful number instead of stalling at zero. */}
        <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-3">
          <StatTile
            accent="coral"
            label={t("values.stats.faster.label")}
            value={3}
            suffix={t("values.stats.faster.suffix")}
          />
          <StatTile
            accent="violet"
            label={t("values.stats.synced.label")}
            value={0.3}
            decimals={1}
            suffix={t("values.stats.synced.suffix")}
          />
          <StatTile
            accent="ocean"
            label={t("values.stats.anywhere.label")}
            value={100}
            suffix={t("values.stats.anywhere.suffix")}
          />
        </div>
      </div>
    </section>
  );
}
